import { reservationsApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { toast } from 'sonner';
import { LogIn, Armchair, Flag, Ban } from 'lucide-react';

// Status colours — the rail on the card and the label everywhere else read from this.
const STATUS_CONFIG = {
  pending:   { label: 'pending', tone: null,   color: 'bg-paper text-ink-soft border-ink-faint', rail: 'bg-ink-faint',   word: 'text-ink-soft' },
  confirmed: { label: 'confirmed', tone: 'info', color: 'bg-signal-info-tint text-signal-info border-signal-info-line',     rail: 'bg-signal-info-line',    word: 'text-signal-info' },
  arrived:   { label: 'arrived', tone: 'warn',   color: 'bg-signal-warn-tint text-signal-warn border-signal-warn-line',  rail: 'bg-signal-warn-line',   word: 'text-signal-warn' },
  seated:    { label: 'seated', tone: 'ok',    color: 'bg-signal-ok-tint text-signal-ok border-signal-ok-line', rail: 'bg-signal-ok-line', word: 'text-signal-ok' },
  completed: { label: 'completed', tone: null, color: 'bg-surface-2 text-ink-soft border-hairline',  rail: 'bg-ink-faint',   word: 'text-ink-faint' },
  cancelled: { label: 'cancelled', tone: 'stop', color: 'bg-signal-stop-tint text-signal-stop border-signal-stop-line',     rail: 'bg-signal-stop-line',    word: 'text-signal-stop' },
  no_show:   { label: 'no-show', tone: 'stop',   color: 'bg-signal-stop-tint text-signal-stop border-signal-stop-line',        rail: 'bg-signal-stop-line',     word: 'text-signal-stop' },
};

// The one obvious next step for a booking, given its status and whether it has a table.
// `null` means "the next step is to get a table" — the parent shows Suggest instead.
const nextStep = (status, hasTable) => {
  if ((status === 'pending' || status === 'confirmed') && hasTable) {
    return { action: 'arrive', label: 'Arrived', icon: LogIn };
  }
  if (status === 'arrived') {
    return hasTable
      ? { action: 'seat', label: 'Seat', icon: Armchair }
      : null;
  }
  if (status === 'seated') return { action: 'complete', label: 'Complete', icon: Flag };
  return null;
};

// Every action available right now — for the expanded row and the edit dialog.
const allSteps = (status, hasTable) => {
  const steps = [];
  if (status === 'pending' || status === 'confirmed') {
    steps.push({ action: 'arrive', label: 'Arrived', icon: LogIn });
    if (hasTable) steps.push({ action: 'seat', label: 'Seat now', icon: Armchair, strong: true });
    steps.push({ action: 'noshow', label: 'No-show', icon: Ban, danger: true });
  } else if (status === 'arrived') {
    if (hasTable) steps.push({ action: 'seat', label: 'Seat', icon: Armchair, strong: true });
    steps.push({ action: 'noshow', label: 'No-show', icon: Ban, danger: true });
  } else if (status === 'seated') {
    steps.push({ action: 'complete', label: 'Complete', icon: Flag, strong: true });
  }
  return steps;
};

const runAction = async (action, reservation, onStatusChange) => {
  try {
    if (action === 'arrive') {
      await reservationsApi.arrive(reservation.id);
      toast.success(`${reservation.customer_name} marked as arrived`);
    } else if (action === 'seat') {
      await reservationsApi.seat(reservation.id, reservation.table_id);
      toast.success(`${reservation.customer_name} seated`);
    } else if (action === 'complete') {
      await reservationsApi.complete(reservation.id);
      toast.success(`${reservation.customer_name}: table complete`);
    } else if (action === 'noshow') {
      await reservationsApi.noshow(reservation.id);
      toast.success(`${reservation.customer_name} marked no-show`);
    } else {
      return;
    }
    onStatusChange?.();
  } catch (err) {
    toast.error(`Couldn't do that: ${err.response?.data?.detail || 'try again'}`);
  }
};

/**
 * `variant="primary"` — the single contextual next-step button for a resting card.
 *   Renders nothing when the next step is "get a table" (the parent shows Suggest).
 * `variant="full"` — every action available now, as a row (expanded card + edit dialog).
 */
const BookingStatusActions = ({ reservation, onStatusChange, variant = 'full' }) => {
  const hasTable = !!reservation.table_id;

  if (variant === 'primary') {
    const step = nextStep(reservation.status, hasTable);
    if (!step) return null;
    return (
      <Button
        size="sm"
        className="h-9 px-4 text-[13px] font-semibold bg-verlo hover:bg-verlo-hover"
        onClick={(e) => { e.stopPropagation(); runAction(step.action, reservation, onStatusChange); }}
        data-testid={`action-${step.action}-${reservation.id}`}
      >
        <step.icon size={14} className="mr-1.5" />
        {step.label}
      </Button>
    );
  }

  const steps = allSteps(reservation.status, hasTable);
  if (steps.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={`status-actions-${reservation.id}`}>
      {steps.map((s) => (
        <Button
          key={s.action}
          size="sm"
          variant={s.strong ? 'default' : 'outline'}
          className={`h-8 text-[12.5px] ${
            s.strong ? 'bg-verlo hover:bg-verlo-hover'
              : s.danger ? 'text-signal-stop border-signal-stop-line hover:bg-signal-stop-tint hover:text-signal-stop'
              : ''
          }`}
          onClick={(e) => { e.stopPropagation(); runAction(s.action, reservation, onStatusChange); }}
          data-testid={`action-${s.action}-${reservation.id}`}
        >
          <s.icon size={13} className="mr-1.5" />
          {s.label}
        </Button>
      ))}
    </div>
  );
};

export { STATUS_CONFIG };
export default BookingStatusActions;
